import { Button, ButtonGroup } from '@bp/ui-components';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { extensions } from './extensions';
import styles from './TipTapEditor.module.scss';

type TipTapEditorProps = {
  name: string;
  label?: string;
  defaultValue: string | null;
  autoFocus?: boolean;
  error?: string;
  isDense?: boolean;
  menu?: 'bubble' | 'inplace';
  alwaysShowMenu?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  className?: string | undefined;
};

export const TipTapEditor = ({
  name,
  label,
  defaultValue,
  autoFocus,
  error,
  isDense,
  menu = 'inplace',
  alwaysShowMenu,
  onChange,
  onBlur,
  className,
}: TipTapEditorProps) => {
  const { t } = useTranslation();

  const editorClasses = classNames(styles.editor, 'has-scrollbar');

  // biome-ignore lint/correctness/useExhaustiveDependencies: <initial value>
  const initialContent = useMemo(() => (defaultValue ? JSON.parse(defaultValue) : null), []);

  const editor = useEditor({
    extensions,
    immediatelyRender: true,
    content: initialContent,
    onUpdate: ({ editor }) => {
      if (onChange && !editor.isDestroyed) {
        onChange(JSON.stringify(editor.getJSON()));
      }
    },
    onBlur: ({ editor }) => {
      if (onBlur && !editor?.isDestroyed) {
        onBlur(JSON.stringify(editor.getJSON()));
      }
    },
    editorProps: {
      attributes: {
        class: editorClasses,
      },
    },
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return null;
      return {
        isH1: editor.isFocused && editor.isActive('heading', { level: 3 }),
        isH2: editor.isFocused && editor.isActive('heading', { level: 4 }),
        isH3: editor.isFocused && editor.isActive('heading', { level: 5 }),
        isH4: editor.isFocused && editor.isActive('heading', { level: 6 }),
        isBulletList: editor.isFocused && editor.isActive('bulletList'),
        isOrderedList: editor.isFocused && editor.isActive('orderedList'),
        isBlockquote: editor.isFocused && editor.isActive('blockquote'),
        isTaskList: editor.isFocused && editor.isActive('taskList'),
        isBold: editor.isFocused && editor.isActive('bold'),
        isItalic: editor.isFocused && editor.isActive('italic'),
        isStrike: editor.isFocused && editor.isActive('strike'),
        isLink: editor.isFocused && editor.isActive('link'),
      };
    },
  });

  return (
    <div
      className={classNames(
        styles['tip-tap-editor'],
        { [styles.empty]: editor.isEmpty, [styles['show-menu']]: alwaysShowMenu },
        className,
      )}
    >
      {menu === 'bubble' && editor && !editor.isDestroyed && (
        <BubbleMenu editor={editor}>
          <ButtonGroup className={styles['button-group']}>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              hierarchy={editor.isActive('heading', { level: 3 }) ? 'primary' : 'secondary'}
            >
              H1
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              hierarchy={editor.isActive('heading', { level: 4 }) ? 'primary' : 'secondary'}
            >
              H2
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
              hierarchy={editor.isActive('heading', { level: 5 }) ? 'primary' : 'secondary'}
            >
              H3
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
              hierarchy={editor.isActive('heading', { level: 6 }) ? 'primary' : 'secondary'}
            >
              H4
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              hierarchy={editor.isActive('bulletList') ? 'primary' : 'secondary'}
            >
              {t('tiptap.bulletList')}
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              hierarchy={editor.isActive('orderedList') ? 'primary' : 'secondary'}
            >
              {t('tiptap.orderedList')}
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleBold().run()}
              hierarchy={editor.isActive('bold') ? 'primary' : 'secondary'}
            >
              {t('tiptap.bold')}
            </Button>
            <Button
              height='s'
              onClick={() => editor.chain().focus().toggleItalic().run()}
              hierarchy={editor.isActive('italic') ? 'primary' : 'secondary'}
            >
              {t('tiptap.italic')}
            </Button>
          </ButtonGroup>
        </BubbleMenu>
      )}

      {menu === 'inplace' && editor && !editor.isDestroyed && (
        <div className={styles['inline-menu']}>
          <button
            className={editorState?.isH1 ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H1
          </button>
          <button
            className={editorState?.isH2 ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          >
            H2
          </button>
          <button
            className={editorState?.isH3 ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          >
            H3
          </button>
          <button
            className={editorState?.isH4 ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
          >
            H4
          </button>

          <div className={styles.divider} />

          <button
            className={editorState?.isBold ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            {t('tiptap.bold')}
          </button>
          <button
            className={editorState?.isItalic ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            {t('tiptap.italic')}
          </button>
          <button
            className={editorState?.isStrike ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            {t('tiptap.strike')}
          </button>

          <div className={styles.divider} />

          <button
            className={editorState?.isBulletList ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            {t('tiptap.bulletList')}
          </button>
          <button
            className={editorState?.isOrderedList ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            {t('tiptap.orderedList')}
          </button>
          <button
            className={editorState?.isBlockquote ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            {t('tiptap.blockquote')}
          </button>
          <button
            className={editorState?.isTaskList ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          >
            {t('tiptap.taskList')}
          </button>

          <div className={styles.divider} />

          <button
            className={editorState?.isLink ? styles.active : undefined}
            type='button'
            onClick={() => editor.chain().focus().toggleLink().run()}
          >
            {t('tiptap.link')}
          </button>
        </div>
      )}

      <EditorContent
        className={styles.wrapper}
        id={`tip-tap-${name}`}
        editor={editor}
        label={label}
        name={name}
        autoFocus={autoFocus}
      />

      {!isDense && <div className={styles['error-message']}>{error}</div>}
    </div>
  );
};
